import { core, usAutocompletePro } from 'smartystreets-javascript-sdk';
import { string as zString } from 'zod';

const { Lookup: AutocompleteProLookup } = usAutocompletePro;
const { ClientBuilder, SharedCredentials } = core;

type SmartyClient = core.Client<
  usAutocompletePro.Lookup,
  usAutocompletePro.Lookup
>;

export type Suggestion = usAutocompletePro.Suggestion;

const SMARTY_API_KEY = zString().parse(process.env.PUBLIC_SMARTY_API_KEY);

let client: SmartyClient | null = null;

export function reset() {
  client = null;
}

function getSmartyClient(): SmartyClient {
  if (client === null) {
    const credentials = new SharedCredentials(SMARTY_API_KEY);
    const clientBuilder = new ClientBuilder(credentials).withLicenses([
      'us-autocomplete-pro-cloud',
    ]);
    client = clientBuilder.buildUsAutocompleteProClient();
  }

  return client;
}

function formatSuggestion({
  streetLine,
  secondary,
  city,
  state,
  zipcode,
  entries,
}: Suggestion) {
  return `${streetLine} ${secondary} (${entries}) ${city} ${state} ${zipcode}`;
}

type SuggestionNoSecondaryEntries = {
  hasSecondaryEntries: false;
};

export type SuggestionHasSecondaryEntries = {
  hasSecondaryEntries: true;
  suggestion: Suggestion;
};

export function suggestionHasSecondaryEntries(
  suggestion: Suggestion
): SuggestionNoSecondaryEntries | SuggestionHasSecondaryEntries {
  const { entries } = suggestion;
  if (entries < 2) {
    return {
      hasSecondaryEntries: false,
    };
  }

  return {
    hasSecondaryEntries: true,
    suggestion,
  };
}

function createLookup(search: string) {
  const lookup = new AutocompleteProLookup(search);
  lookup.includeOnlyStates = ['contiguous'];
  lookup.maxResults = 4;
  return lookup;
}

export async function suggest(search: string) {
  const client = getSmartyClient();
  const lookup = createLookup(search);
  return await client.send(lookup);
}

export async function suggestSecondary(
  search: string,
  { suggestion }: Pick<SuggestionHasSecondaryEntries, 'suggestion'>
) {
  const client = getSmartyClient();
  const lookup = createLookup(search);
  lookup.selected = formatSuggestion(suggestion);
  lookup.includeOnlyStates = ['contiguous'];
  return await client.send(lookup);
}
